import { io } from "socket.io-client";
import { URL } from "../components/BaseUrl";

class MessagingSocket {
    constructor(token) {
        this.token = token;
        this.socketUrl = URL;
        this.socket = null;
        this.listeners = new Map();
    }

    connect() {
        if (this.socket && this.socket.connected) return;

        this.socket = io(this.socketUrl, {
            auth: {
                token: this.token,
            },
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
        });

        this.socket.on("connect", () => {
            console.log("🔌 Socket connected");
            this.emit("authenticate", { token: this.token });
        });

        this.socket.on("authenticated", (data) => {
            console.log("✅ Socket authenticated:", data);
            if (data.user && data.user.id) {
                this.joinUserRoom(data.user.id);
            }
            this._notifyListeners("authenticated", data);
        });

        this.socket.on("auth_error", (error) => {
            console.error("❌ Socket auth error:", error);
            this._notifyListeners("auth_error", error);
        });

        this.socket.on("disconnect", (reason) => {
            console.log("❌ Socket disconnected:", reason);
            this._notifyListeners("disconnect", reason);
        });

        // Generic event forwarding
        const events = [
            "receive_message",
            "message_sent",
            "image_sent",
            "new_image_message",
            "messages_read",
            "user_typing",
            "user_statuses_list",
            "user_status_change",
            "total_unread_count_updated",
            "unread_count_updated",
            "room_joined"
        ];

        events.forEach((event) => {
            this.socket.on(event, (data) => {
                this._notifyListeners(event, data);
            });
        });
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    // Room Management
    joinUserRoom(userId) {
        this.emit("join_user_room", userId);
    }

    joinThreadRoom(threadId) {
        this.emit("join_thread_room", threadId);
    }

    leaveThreadRoom(threadId) {
        this.emit("leave_thread_room", threadId);
    }

    // Actions
    sendMessage(data) {
        // data: { threadId, content, messageType, tempId, ... }
        this.emit("send_message", data);
    }

    sendImageBase64(data) {
        // data: { threadId, base64Data, fileName, mimeType, tempId }
        this.emit("send_image_base64", data);
    }

    markAsRead(threadId, upToMessageId = null) {
        this.emit("mark_read", { threadId, upToMessageId });
    }

    sendTyping(threadId, isTyping) {
        this.emit(isTyping ? "typing_start" : "typing_stop", { threadId });
    }

    getUserStatuses() {
        this.emit("get_user_statuses");
    }

    getTotalUnreadCount() {
        this.emit("get_total_unread_count", { role: null });
    }

    // Get socket instance
    getSocket() {
        return this.socket;
    }

    // Helper to emit safely
    emit(event, data) {
        if (this.socket && this.socket.connected) {
            this.socket.emit(event, data);
        } else {
            console.warn(`⚠️ Cannot emit '${event}', socket not connected`);
        }
    }

    // Event Listener Management
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);

        // Return unsubscribe function
        return () => this.off(event, callback);
    }

    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }

    _notifyListeners(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach((callback) => {
                try {
                    callback(data);
                } catch (err) {
                    console.error(`Error in listener for '${event}':`, err);
                }
            });
        }
    }
}

export default MessagingSocket;
